import { authHelper } from '../utils/authHelper';
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  CheckCircle2, 
  BarChart3, 
  Calculator, 
  Calendar, 
  Target, 
  TrendingUp,
  Sparkles 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function LandingPage() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);

  useEffect(() => {
    // If user is already logged in and they visit root, redirect to hub
    if (authHelper.isAuthenticated()) {
      navigate('/hub', { replace: true });
      return;
    }

    // Fetch editable content from backend
    const fetchContent = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/landing/content`);
        if (response.ok) {
          const data = await response.json();
          setContent(data);
        }
      } catch (error) {
        console.error('Failed to fetch landing content', error);
      }
    };
    fetchContent();
  }, [navigate]);

  // Default content (fallback)
  const defaultContent = {
    hero: {
      title: "Gestión Profesional de Trading",
      subtitle: "Controla tu disciplina, protege tu capital",
      cta: "Comenzar Prueba Gratis",
      creator_image: "https://customer-assets.emergentagent.com/job_metodo-mobile/artifacts/8yg144hu_BANER%20ENTRENAMIENTO.png"
    },
    features: [
      { 
        title: "Bitácora de Trades",
        description: "Registra cada operación con detalle y analiza tus patrones de trading",
        icon: "BarChart3",
        span: "md:col-span-4"
      },
      {
        title: "Calculadoras Avanzadas",
        description: "Gestión de riesgo y tamaño de posición en tiempo real",
        icon: "Calculator",
        span: "md:col-span-4"
      },
      {
        title: "Calendario de Disciplina",
        description: "Evalúa tu consistencia día a día con métricas visuales",
        icon: "Calendar",
        span: "md:col-span-4"
      },
      {
        title: "Plan de Trading Personalizado",
        description: "Define y sigue tu estrategia con metas claras",
        icon: "Target",
        span: "md:col-span-6"
      },
      {
        title: "Estadísticas en Tiempo Real",
        description: "Dashboards interactivos con tu progreso y rendimiento",
        icon: "TrendingUp",
        span: "md:col-span-6"
      }
    ],
    pricing: {
      app_mobile: {
        name: "App Móvil",
        price: 3,
        currency: "$",
        period: "mes",
        trial_days: 7,
        cta: "Comenzar Prueba Gratis",
        features: [
          "7 días de prueba gratuita",
          "Acceso completo a todas las funciones",
          "Sincronización en la nube",
          "Soporte técnico prioritario",
          "Actualizaciones automáticas"
        ]
      },
      web_platform: {
        name: "Plataforma Web",
        price: 2500,
        currency: "$",
        period: "único",
        description: "Plataforma educativa completa con cursos, videos, exámenes y feedback con IA",
        coming_soon: true
      }
    },
    footer: {
      copyright: "2026 MÉTODO 11",
      links: [
        { label: "Términos", href: "/terms" },
        { label: "Privacidad", href: "/privacy" },
        { label: "Contacto", href: "/contact" }
      ]
    }
  };

  const data = content || defaultContent;

  const iconMap = {
    BarChart3: BarChart3,
    Calculator: Calculator,
    Calendar: Calendar,
    Target: Target,
    TrendingUp: TrendingUp
  };

  const handleCTAClick = () => {
    navigate('/checkout/app-mobile');
  };

  const handleWebPlatformClick = () => {
    // Check if coming soon
    if (data.pricing.web_platform.coming_soon) {
      alert('🚀 Próximamente: Esta plataforma estará disponible muy pronto. ¡Mantente atento!');
      return;
    }
    navigate('/checkout/web-platform');
  };

  return (
    <div className="bg-[#02040A] min-h-screen font-manrope text-white overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 backdrop-blur-xl bg-[#02040A]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#C5A14C] to-[#B08F3C] flex items-center justify-center font-bold text-[#02040A] text-lg">
              M11
            </div>
            <span className="font-outfit font-black text-xl">MÉTODO 11</span>
          </motion.div>
          
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/login')}
            className="text-sm font-semibold text-white/80 hover:text-white transition-colors"
            data-testid="header-login-btn"
          >
            Iniciar Sesión
          </motion.button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
        {/* Background Gradient Glow */}
        <div className="absolute top-[-20%] left-[10%] w-[80%] h-[80%] rounded-full bg-[radial-gradient(circle_at_center,#1E3A8A_0%,transparent_60%)] opacity-50 blur-[100px] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 mb-6">
                <Sparkles className="w-4 h-4 text-[#C5A14C]" />
                <span className="text-sm text-white/70">Eleva tu trading al siguiente nivel</span>
              </div>
              
              <h1 className="font-outfit text-5xl md:text-6xl lg:text-7xl tracking-tighter font-black text-transparent bg-clip-text bg-gradient-to-b from-white to-white/70 mb-6">
                {data.hero.title}
              </h1>
              
              <p className="text-lg md:text-xl text-[#A1A1AA] leading-relaxed mb-8">
                {data.hero.subtitle}
              </p>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleCTAClick}
                className="bg-[#C5A14C] text-[#02040A] font-bold rounded-full px-8 py-4 hover:bg-[#B08F3C] hover:shadow-[0_0_20px_rgba(197,161,76,0.4)] transition-all duration-300 flex items-center justify-center gap-2"
                data-testid="hero-cta"
              >
                {data.hero.cta}
                <ArrowRight className="w-5 h-5" />
              </motion.button>
              
              <p className="text-sm text-white/50 mt-4">
                ✨ Sin tarjeta de crédito • 7 días gratis • Cancela cuando quieras
              </p>
            </motion.div>

            {/* Right: Trading Dashboard Mockup */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-gradient-to-br from-[#1E3A8A]/20 to-[#02040A]/40">
                <img 
                  src={data.hero.creator_image || "https://customer-assets.emergentagent.com/job_metodo-mobile/artifacts/8yg144hu_BANER%20ENTRENAMIENTO.png"}
                  alt="Creadora de MÉTODO 11"
                  className="w-full h-auto mix-blend-luminosity hover:mix-blend-normal transition-all duration-500"
                  style={{ 
                    filter: 'contrast(1.1) brightness(1.05)',
                    maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 70%, rgba(0,0,0,0) 100%)',
                    WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 70%, rgba(0,0,0,0) 100%)'
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#02040A] via-[#02040A]/20 to-transparent opacity-80" />
              </div>
              
              {/* Floating Stats Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="absolute -bottom-6 -left-6 bg-gradient-to-b from-white/10 to-transparent backdrop-blur-xl border border-white/20 rounded-2xl p-4 shadow-xl"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-[#C5A14C]/20 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-[#C5A14C]" />
                  </div>
                  <div>
                    <p className="text-xs text-white/60">Win Rate</p>
                    <p className="text-2xl font-black text-white">94.2%</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-outfit text-3xl md:text-5xl tracking-tight font-bold text-white mb-4">
              Todo lo que necesitas para dominar el trading
            </h2>
            <p className="text-lg text-[#A1A1AA] max-w-2xl mx-auto">
              Herramientas profesionales diseñadas para traders que toman en serio su disciplina y crecimiento
            </p>
          </motion.div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {data.features.map((feature, index) => {
              const Icon = iconMap[feature.icon] || BarChart3;
              return (
                <motion.div
                  key={feature.title || index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`${feature.span || 'md:col-span-4'} bg-gradient-to-b from-white/5 to-transparent backdrop-blur-md border border-white/10 rounded-3xl p-8 hover:border-white/20 hover:-translate-y-1 transition-all duration-500 group`}
                  data-testid={`feature-card-${index}`}
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#C5A14C]/10 border border-[#C5A14C]/20 flex items-center justify-center mb-6 group-hover:bg-[#C5A14C]/20 transition-colors">
                    <Icon className="w-7 h-7 text-[#C5A14C]" />
                  </div>
                  <h3 className="font-outfit text-xl font-bold text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-[#A1A1AA] leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-outfit text-3xl md:text-5xl tracking-tight font-bold text-white mb-4">
              Elige tu plan
            </h2>
            <p className="text-lg text-[#A1A1AA]">
              Comienza gratis y crece con nosotros
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* PRIMERO: Plataforma Web $2500 */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-b from-[#1E3A8A]/60 to-[#02040A] backdrop-blur-xl border border-[#C5A14C]/40 rounded-3xl p-8 relative overflow-hidden shadow-[0_0_40px_rgba(197,161,76,0.1)]"
              data-testid="pricing-tier-web-platform"
            >
              <div className="absolute top-0 right-0 w-48 h-48 bg-[#C5A14C]/10 rounded-full blur-3xl" />
              
              <div className="relative z-10">
                <div className="inline-flex items-center bg-[#C5A14C]/20 text-[#C5A14C] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider border border-[#C5A14C]/30 mb-4">
                  {data.pricing.web_platform.coming_soon ? 'Próximamente' : 'Premium'}
                </div>
                
                <h3 className="font-outfit text-2xl font-bold text-white mb-2">
                  {data.pricing.web_platform.name}
                </h3>
                
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-5xl font-black text-[#C5A14C]">
                    ${data.pricing.web_platform.price || 2500}
                  </span>
                  <span className="text-lg text-white/60">
                    {data.pricing.web_platform.period || 'pago único'}
                  </span>
                </div>

                <p className="text-sm text-white/80 mb-8">
                  {data.pricing.web_platform.description}
                </p>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleWebPlatformClick}
                  className={`w-full font-bold rounded-full px-8 py-4 transition-all duration-300 flex items-center justify-center gap-2 ${
                    data.pricing.web_platform.coming_soon 
                      ? 'bg-white/10 text-white/60 border border-white/20 cursor-not-allowed' 
                      : 'bg-[#C5A14C] text-[#02040A] hover:bg-[#B08F3C] hover:shadow-[0_0_20px_rgba(197,161,76,0.4)]'
                  }`}
                  data-testid="pricing-cta-web-platform"
                >
                  {data.pricing.web_platform.coming_soon ? 'Próximamente' : 'Conocer Más'}
                  <ArrowRight className="w-5 h-5" />
                </motion.button>

                {/* Política de Devolución */}
                {!data.pricing.web_platform.coming_soon && (
                  <div className="mt-4 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                    <p className="text-xs font-semibold text-amber-400 text-center">
                      📋 Todas las ventas son finales • No hay devoluciones
                    </p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* SEGUNDO: App Móvil $3/mes */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-gradient-to-b from-white/10 to-transparent backdrop-blur-xl border border-white/20 rounded-3xl p-8 relative overflow-hidden"
              data-testid="pricing-tier-app-mobile"
            >
              <div className="relative z-10">
                <div className="inline-flex items-center bg-white/10 text-white/70 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider border border-white/10 mb-4">
                  7 días gratis
                </div>
                
                <h3 className="font-outfit text-2xl font-bold text-white mb-2">
                  {data.pricing.app_mobile.name}
                </h3>
                
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-5xl font-black text-white">
                    {data.pricing.app_mobile.currency}{data.pricing.app_mobile.price}
                  </span>
                  <span className="text-lg text-white/60">
                    / {data.pricing.app_mobile.period}
                  </span>
                </div>

                <ul className="space-y-3 mb-8">
                  {data.pricing.app_mobile.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-white/60 shrink-0 mt-0.5" />
                      <span className="text-sm text-white/70">{feature}</span>
                    </li>
                  ))}
                </ul>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleCTAClick}
                  className="w-full bg-white/10 text-white font-bold rounded-full px-8 py-4 hover:bg-white/20 transition-all duration-300 flex items-center justify-center gap-2"
                  data-testid="pricing-cta-app-mobile"
                >
                  {data.pricing.app_mobile.cta}
                  <ArrowRight className="w-5 h-5" />
                </motion.button>

                {/* Política de Devolución */}
                <div className="mt-4 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                  <p className="text-xs font-semibold text-amber-400 text-center">
                    📋 Todas las ventas son finales • No hay devoluciones
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#C5A14C] to-[#B08F3C] flex items-center justify-center font-bold text-[#02040A] text-sm">
                M11
              </div>
              <span className="text-sm text-white/60">
                © {data.footer.copyright}. Todos los derechos reservados.
              </span>
            </div>
            
            <div className="flex items-center gap-6">
              {data.footer.links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-sm text-white/60 hover:text-white transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
